import type { ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import AccountAccessUserInput from 'components/Account/AccountAccess/AccountAccessUserInput'
import AccountAccessSocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'

export const Card = styled.div`
  width: 100%;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(20)};
`

export const SuccessCard = styled.div`
  width: 100%;
  padding: ${rem(4)} 0;
`

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

export const Title = styled.h3`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(20)};
  line-height: ${rem(24)};
  font-weight: 600;
`

export const SuccessTitle = styled(Heading)`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const BackRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

export const OrRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    display: block;
  }
`

export const OrLabel = styled(BodyText)`
  white-space: nowrap;
  flex-shrink: 0;
`

export const StyledAccountAccessUserInput = styled(AccountAccessUserInput)`
  > span,
  label > span {
    margin-bottom: ${rem(44)};
  }

  > span .variant-caption,
  label > span .variant-caption {
    bottom: ${rem(-8)};
  }
`

export const StyledPasswordInput = styled(PasswordInput)`
  label > span {
    margin-bottom: ${rem(8)};
  }
`

export const StyledSocialButtons = styled(AccountAccessSocialButtons)`
  margin-bottom: 0;

  &.inline {
    gap: ${rem(16)};
  }

  button,
  a {
    border-radius: ${props => props.theme.borderRadius.round};
    min-height: ${rem(48)};
  }
`

export const SuccessHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

export function ErrorBanner({ error, genericMessage }: { error: any; genericMessage: string }) {
  if (!error?.message && error?.status !== 500) {
    return null
  }

  return (
    <MessageBanner kind="critical">
      {error?.status === 500 ? genericMessage : error.message}
    </MessageBanner>
  )
}

export function StepBackButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <BackRow>
      <TextButton kind="tertiary" variant="default" size="small" onClick={onClick}>
        {label}
      </TextButton>
    </BackRow>
  )
}

export function renderStrongText(chunks: ReactNode) {
  return <strong>{chunks}</strong>
}

export function renderBoldText(chunks: ReactNode) {
  return <b>{chunks}</b>
}

export function PendingButtonContent({ pending, label }: { pending: boolean; label: string }) {
  if (pending) {
    return <LoadingSpinner size={18} inline />
  }

  return label
}
